import { TFunction } from 'i18next';
import * as yup from 'yup';

import { RoomAssignType } from 'components/BreakoutRooms/utils';
import constants, { CUSTOM_ERROR_NAMES } from 'Constants';
import i18n from 'i18n/config';
import { languages } from 'i18n/utils';
import { WorkspaceRole } from 'modules/CreateWorkspaceModal/types';
import { SECURITY_CODE_LENGTH } from 'modules/EndToEndEncryption/SecurityCode/securityCodeConstants';
import { ChatPrivacyEnum } from 'modules/Messenger/components/CreateNewChatModal/ChatPrivacyField/ChatPrivacyEnum';
import { DefaultRoleTypesEnum, getReservedRoleNames } from 'modules/Workspaces/hooks/useCreateWorkspaceRole/utils';
import { isWorkspaceAliasEnabled } from 'modules/Workspaces/WorkspacesProvider/utils';
import { checkForDateInFuture } from 'utils/utils';

import './schemaExtensions';
import { getCreateNewRoom } from './getCreateNewRoom/getCreateNewRoom';

export const FEEDBACK_MAX_CHARS = 2048;
const MAX_DISPLAY_NAME_CHARS = 255;
const MAX_WORKSPACE_NAME_CHARS = 255;

export const getAliasValidation = () =>
  yup
    .string()
    .required()
    .min(3)
    .max(50)
    .matches(/^[a-z0-9-]+$/i)
    .trim();

const getCreateRoom = (t: TFunction) =>
  yup
    .object()
    .shape({
      roomTitle: yup.string().required().min(3).max(50).trim(),
      isWaitingListEnabled: yup.boolean().required(),
      roomAlias: getAliasValidation(),
      startDate: yup.date().nullable().optional(),
      startTime: yup.date().nullable().optional(),
      description: yup.string().max(constants.MAX_TOPIC_LENGTH).optional().trim(),
    })
    .test(CUSTOM_ERROR_NAMES.FUTURE_DATE, t('future_date_error'), ({ startDate, startTime }) =>
      checkForDateInFuture({ startDate, startTime })
    );

const getJoinRoom = () =>
  yup.object().shape({
    name: yup.string().required().max(MAX_DISPLAY_NAME_CHARS).trim(),
    acceptedToS: yup.boolean().required().isTrue(),
  });

const getJoinMatrixRoom = (t: TFunction) =>
  yup.object().shape({
    roomId: yup
      .string()
      .matches(/^![A-Za-z0-9_-]+:[A-Za-z0-9.-]+$/, t('invalid_room_id'))
      .required(),
  });

const getMatrixHomeserver = () =>
  yup.object().shape({
    homeserver: yup.string().required().max(100),
  });

export const MAX_CHAT_MESSAGE_LENGTH = 2000;

const getMessageValidation = () =>
  yup
    .object({
      message: yup.string().optional().max(MAX_CHAT_MESSAGE_LENGTH).trim(),
      files: yup.mixed().optional(),
    })
    .test('one-of-two', '', ({ message, files }) => (message || files ? true : false));

const getFeedbackValidation = () =>
  yup.object().shape({ feedbackComment: yup.string().required().max(FEEDBACK_MAX_CHARS, i18n.t('feedback.too_long')) });

const getUpdateDisplayName = () =>
  yup.object().shape({
    displayName: yup
      .string()
      .required(i18n.t('display_name_required'))
      .max(MAX_DISPLAY_NAME_CHARS, i18n.t('display_name_too_long', { maxLength: MAX_DISPLAY_NAME_CHARS }))
      .trim(),
  });

const getRenameBreakoutRoom = () =>
  yup.object().shape({
    roomTitle: yup.string().max(100).required().trim(),
  });

const getBreakoutSettings = (t: TFunction) => ({
  allowReturnToMain: yup.boolean(),
  timeLimitHours: yup.number(),
  timeLimitMinutes: yup.number(),
  displayTimeLeft: yup.boolean(),
  timeLimit: yup.number().nullable().optional(),
  isTimeLimitActive: yup.boolean().test('isTimeLimitActive', t('breakout_rooms.time_limit_error'), function (isActive) {
    if (!isActive) return true;

    const { timeLimit, isTimeLimitActive } = this.parent;

    if (isTimeLimitActive && !timeLimit) {
      return this.createError({
        path: this.path,
        message: t('breakout_rooms.time_limit_error'),
      });
    }

    return true;
  }),
});

const transformTimeLimit = (roomSettings: any) => {
  const { timeLimitHours, timeLimitMinutes, isTimeLimitActive, ...rest } = roomSettings;
  const minutes = isTimeLimitActive ? timeLimitHours * 60 + timeLimitMinutes : 0;

  return { ...rest, isTimeLimitActive, timeLimit: minutes };
};

const getBreakoutRoomSettings = (t: TFunction) =>
  yup
    .object()
    .shape(getBreakoutSettings(t))
    .transform((settings) => transformTimeLimit(settings));

export const getCreateBreakoutRoom = (t: TFunction) =>
  yup
    .object()
    .shape({
      roomCount: yup.number().required(),
      assignment: yup.mixed<RoomAssignType>().oneOf(Object.values(RoomAssignType)).required(),
      ...getBreakoutSettings(t),
    })
    .transform((settings) => transformTimeLimit(settings));

export const getDateInput = () =>
  yup.object().shape({
    startDateInput: yup.string().required(),
    endDateInput: yup.string(),
  });

export const getInviteeValidation = () =>
  yup.string().required().trim().validEmailOrMatrixId(i18n.t('workspaces.email_validation_error'));

export const getWorkspaceInviteSchema = () =>
  yup.object({
    invitees: yup.array().of(getInviteeValidation()).default([]),
    role: yup.string().nullable(),
  });

export const getWorkspaceRolesSchema = (existingRoles: WorkspaceRole[], excludeRoleId?: string) => {
  i18n.loadLanguages(languages);

  const existingDefaultRoleTypes = existingRoles.reduce((acc, { id, defaultRoleType }) => {
    if (excludeRoleId === id) return acc;
    if (defaultRoleType) acc.push(defaultRoleType);
    return acc;
  }, [] as DefaultRoleTypesEnum[]);

  const reservedNames = getReservedRoleNames(existingDefaultRoleTypes);
  const rolesToCheck = excludeRoleId ? existingRoles.filter((role) => role.id !== excludeRoleId) : existingRoles;

  return yup.object({
    roleName: yup
      .string()
      .required(i18n.t('workspaces.role_name_required'))
      .trim()
      .uniqueRoleName(i18n.t('workspaces.role_name_already_exists'), rolesToCheck, reservedNames)
      .reservedRoleName(i18n.t('workspaces.role_name_reserved'), existingDefaultRoleTypes, reservedNames),
    permissions: yup.object().default({}),
  });
};

const getWorkspaceNameValidation = () =>
  yup.object().shape({
    workspaceName: yup
      .string()
      .required(i18n.t('workspaces.workspace_name_required'))
      .max(
        MAX_WORKSPACE_NAME_CHARS,
        i18n.t('workspaces.workspace_name_too_long', { maxLength: MAX_WORKSPACE_NAME_CHARS })
      )
      .trim(),
  });

const getWorkspaceSlugValidation = () =>
  yup.object().shape({
    workspaceSlug: yup
      .string()
      .required()
      .min(3)
      .max(50)
      .matches(/^[a-z0-9-]+$/i)
      .trim(),
  });

const workspaceAliasValidation = isWorkspaceAliasEnabled
  ? yup
      .string()
      .required()
      .min(3)
      .max(50)
      .matches(/^[a-z0-9-]+$/i)
  : yup.string().optional();

const getWorkspaceCreateSchema = () =>
  yup.object({
    workspaceName: yup
      .string()
      .required(i18n.t('workspaces.workspace_name_required'))
      .max(
        MAX_WORKSPACE_NAME_CHARS,
        i18n.t('workspaces.workspace_name_too_long', { maxLength: MAX_WORKSPACE_NAME_CHARS })
      )
      .trim(),
    workspaceAlias: workspaceAliasValidation,
    parentWorkspaceId: yup.string().nullable(),
  });

const getSecurityCodeValidation = () =>
  yup.object().shape({
    securityCode: yup
      .string()
      .required(i18n.t('verification.security_code_required'))
      .length(SECURITY_CODE_LENGTH, i18n.t('verification.security_code_must_be_6_digits'))
      .matches(/^\d+$/, i18n.t('verification.security_code_must_be_numbers')),
    confirmSecurityCode: yup
      .string()
      .required(i18n.t('verification.security_code_confirm_required'))
      .matches(/^\d+$/, i18n.t('verification.security_code_must_be_numbers'))
      .oneOf([yup.ref('securityCode')], i18n.t('verification.security_codes_must_match')),
  });

const getEnterSecurityCodeValidation = () =>
  yup.object().shape({
    securityCode: yup
      .string()
      .required(i18n.t('verification.security_code_required'))
      .length(SECURITY_CODE_LENGTH, i18n.t('verification.security_code_must_be_6_digits'))
      .matches(/^\d+$/, i18n.t('verification.security_code_must_be_numbers')),
  });

const getNewChatSchema = () =>
  yup.object({
    chatName: yup.string().trim().max(50).default(''),
    chatPrivacy: yup.string().oneOf(Object.values(ChatPrivacyEnum)).required(),
    invitees: yup.array().of(getInviteeValidation()).default([]),
  });

const getAddMembersSchema = () =>
  yup.object({
    invitees: yup.array().of(getInviteeValidation()).min(1).default([]),
  });

const MATRIX_ID_PATTERN = /^@[a-zA-Z0-9._=-]+:[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const getVideoInviteeValidation = () =>
  yup
    .string()
    .required()
    .trim()
    .test('valid-mxid-or-email', i18n.t('workspaces.email_validation_error'), (value) => {
      if (!value) return false;
      const trimmed = value.trim();
      return MATRIX_ID_PATTERN.test(trimmed) || EMAIL_PATTERN.test(trimmed);
    });

const getVideoAddMembersSchema = () =>
  yup.object({
    invitees: yup.array().of(getVideoInviteeValidation()).min(1).default([]),
  });

export const Schemas = {
  getCreateRoom,
  getJoinRoom,
  getJoinMatrixRoom,
  getMatrixHomeserver,
  getMessageValidation,
  getFeedbackValidation,
  getUpdateDisplayName,
  getCreateNewRoom,
  getCreateBreakoutRoom,
  getRenameBreakoutRoom,
  getBreakoutRoomSettings,
  getDateInput,
  getInviteeValidation,
  getWorkspaceInviteSchema,
  getWorkspaceRolesSchema,
  getWorkspaceNameValidation,
  getWorkspaceSlugValidation,
  getWorkspaceCreateSchema,
  getSecurityCodeValidation,
  getEnterSecurityCodeValidation,
  getNewChatSchema,
  getAddMembersSchema,
  getVideoAddMembersSchema,
};
